import { FC, useEffect, useState } from "react"
import {
  Wallet,
  Plus,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  Receipt
} from "lucide-react"
import {
  Channel,
  ChannelBalance,
  ChannelTransaction,
  ChannelTransactionType
} from "../../../../types"
import {
  useGetChannelTransactionsQuery,
  useAddFundsMutation,
  useGetChannelAutoReloadQuery,
  useUpdateChannelAutoReloadMutation
} from "../../../../redux/queries"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import Modal from "../../modal"
import { PrimaryBtn, LightBtn } from "../../buttons"
import { Link } from "react-router-dom"
import If from "../../if"
import BBSwitch from "../../form/Switch"

interface Props {
  orgId: string
  balance?: ChannelBalance
  channels: Channel[]
  isAdmin: boolean
  canManageAutoReload?: boolean
  canAddFunds?: boolean
  onBalanceRefresh?: () => void
}

const BalanceTab: FC<Props> = ({
  orgId,
  balance,
  channels,
  isAdmin,
  canManageAutoReload,
  canAddFunds,
  onBalanceRefresh
}) => {
  const dispatch = useAppDispatch()
  const [showAddFundsModal, setShowAddFundsModal] = useState(false)
  const [addFundsAmount, setAddFundsAmount] = useState("")
  const [autoReloadEnabled, setAutoReloadEnabled] = useState(false)
  const [minBalanceDollars, setMinBalanceDollars] = useState("")
  const [reloadAmountDollars, setReloadAmountDollars] = useState("")

  const {
    data: transactions,
    isFetching,
    refetch: refetchTransactions
  } = useGetChannelTransactionsQuery(
    { org_id: orgId, limit: 50 },
    { skip: !orgId }
  )

  const { data: autoReloadSettings, isFetching: isFetchingAutoReload } =
    useGetChannelAutoReloadQuery(orgId, { skip: !orgId })

  const [addFunds, { isLoading: isAddingFunds }] = useAddFundsMutation()
  const [updateAutoReload, { isLoading: isSavingAutoReload }] =
    useUpdateChannelAutoReloadMutation()
  const canEditAutoReload = (canManageAutoReload ?? isAdmin) || false
  const canCreateAddFunds = (canAddFunds ?? isAdmin) || canEditAutoReload

  const handleAddFunds = async () => {
    const amountCents = Math.round(parseFloat(addFundsAmount) * 100)
    if (isNaN(amountCents) || amountCents <= 0) {
      dispatch(
        pushToast({
          msg: "Please enter a valid amount",
          theme: "danger"
        })
      )
      return
    }

    try {
      const result = await addFunds({
        org_id: orgId,
        amount_cents: amountCents,
        description: "Manual balance recharge"
      }).unwrap()

      if (result.status === "success") {
        dispatch(
          pushToast({
            msg: "Recharge invoice created successfully",
            theme: "success"
          })
        )
        onBalanceRefresh?.()
        refetchTransactions()
        setShowAddFundsModal(false)
        setAddFundsAmount("")
      } else {
        dispatch(
          pushToast({
            msg: result.message || "Failed to add funds",
            theme: "danger"
          })
        )
      }
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to add funds",
          theme: "danger"
        })
      )
    }
  }

  useEffect(() => {
    if (autoReloadSettings) {
      setAutoReloadEnabled(!!autoReloadSettings.auto_reload_enabled)
      setMinBalanceDollars(
        autoReloadSettings.min_balance_cents
          ? (autoReloadSettings.min_balance_cents / 100).toFixed(2)
          : ""
      )
      setReloadAmountDollars(
        autoReloadSettings.reload_amount_cents
          ? (autoReloadSettings.reload_amount_cents / 100).toFixed(2)
          : ""
      )
    }
  }, [autoReloadSettings])

  const handleSaveAutoReload = async () => {
    if (!orgId) return

    const payload: {
      org_id: string
      auto_reload_enabled?: boolean
      min_balance_cents?: number
      reload_amount_cents?: number
    } = {
      org_id: orgId,
      auto_reload_enabled: autoReloadEnabled
    }

    if (autoReloadEnabled) {
      const minCents = Math.round(parseFloat(minBalanceDollars) * 100)
      const reloadCents = Math.round(parseFloat(reloadAmountDollars) * 100)

      if (
        isNaN(minCents) ||
        minCents <= 0 ||
        isNaN(reloadCents) ||
        reloadCents <= 0
      ) {
        dispatch(
          pushToast({
            msg: "Enter valid amounts for minimum balance and reload amount",
            theme: "danger"
          })
        )
        return
      }

      payload.min_balance_cents = minCents
      payload.reload_amount_cents = reloadCents
    }

    try {
      const result = await updateAutoReload(payload).unwrap()
      if (result.status === "success" || result.settings) {
        dispatch(
          pushToast({
            msg: "Auto-reload settings updated",
            theme: "success"
          })
        )
      } else {
        dispatch(
          pushToast({
            msg: result.message || "Failed to update auto-reload",
            theme: "danger"
          })
        )
      }
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to update auto-reload",
          theme: "danger"
        })
      )
    }
  }

  const getTransactionIcon = (type: ChannelTransactionType) => {
    switch (type) {
      case "deposit":
      case "manual_recharge":
        return <ArrowDownCircle size={18} className="text-emerald-500" />
      case "lead_charge":
        return <ArrowUpCircle size={18} className="text-rose-500" />
      case "auto_recharge":
        return <RefreshCw size={18} className="text-blue-500" />
      case "refund":
        return <ArrowDownCircle size={18} className="text-amber-500" />
      default:
        return <Receipt size={18} className="text-gray-400" />
    }
  }

  const getTransactionLabel = (type: ChannelTransactionType) => {
    switch (type) {
      case "deposit":
        return "Deposit"
      case "manual_recharge":
        return "Manual Recharge"
      case "lead_charge":
        return "Lead Charge"
      case "auto_recharge":
        return "Auto Recharge"
      case "refund":
        return "Refund"
      case "adjustment":
        return "Adjustment"
      default:
        return type
    }
  }

  const getChannelName = (channelId: string) => {
    if (channelId === "0") return "—"
    const channel = channels.find(c => c.id === channelId)
    return channel?.name ?? `Channel #${channelId}`
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp, 10) * 1000)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    })
  }

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                <Wallet size={24} />
              </div>
              <div>
                <p className="text-sm text-white/70 font-medium">
                  Channel Balance
                </p>
                <p className="text-xs text-white/50">Pre-funded lead credits</p>
              </div>
            </div>
            {canCreateAddFunds && (
              <button
                onClick={() => setShowAddFundsModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-medium text-sm transition-all backdrop-blur-sm"
              >
                <Plus size={16} />
                Add Funds
              </button>
            )}
          </div>

          <div className="mt-6">
            <span className="text-5xl font-bold tracking-tight">
              ${balance?.balance_dollars ?? "0.00"}
            </span>
          </div>
        </div>
      </div>

      {/* Auto-Reload (Org-wide) */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Auto-Reload (Account-wide)
            </h3>
            <p className="text-sm text-gray-500">
              Configure balance auto-funding for this organization. Applies to
              all channels.
            </p>
          </div>
          <BBSwitch
            enabled={autoReloadEnabled}
            label="Auto-Reload"
            description=""
            className="mt-1"
            switchPosition="left"
            helperText={
              !canEditAutoReload
                ? "Only bank admins or super admins can change auto-reload"
                : undefined
            }
            setEnabled={canEditAutoReload ? setAutoReloadEnabled : undefined}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="min_balance"
              className="block text-sm font-semibold text-gray-700 mb-1"
            >
              Minimum Balance Threshold
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                $
              </span>
              <input
                id="min_balance"
                type="text"
                value={minBalanceDollars}
                onChange={e => setMinBalanceDollars(e.target.value)}
                placeholder="25.00"
                disabled={!autoReloadEnabled || !canEditAutoReload}
                className="appearance-none block w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-gray-50"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Trigger auto-reload when balance drops below this amount.
            </p>
          </div>

          <div>
            <label
              htmlFor="reload_amount"
              className="block text-sm font-semibold text-gray-700 mb-1"
            >
              Reload Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                $
              </span>
              <input
                id="reload_amount"
                type="text"
                value={reloadAmountDollars}
                onChange={e => setReloadAmountDollars(e.target.value)}
                placeholder="100.00"
                disabled={!autoReloadEnabled || !canEditAutoReload}
                className="appearance-none block w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-gray-50"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Amount to invoice and add when auto-reload triggers.
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center gap-3 pt-2 flex-wrap">
          <Link
            to="/account/payment-management"
            className="text-sm font-semibold text-teal-700 hover:text-teal-800"
          >
            Manage billing & payment methods
          </Link>
          <PrimaryBtn
            onClick={handleSaveAutoReload}
            disabled={
              !canEditAutoReload || isSavingAutoReload || isFetchingAutoReload
            }
          >
            {isSavingAutoReload ? "Saving..." : "Save auto-reload"}
          </PrimaryBtn>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            Transaction History
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Recent balance activity
          </p>
        </div>

        <If
          condition={isFetching}
          then={
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-gray-400">
                Loading transactions...
              </div>
            </div>
          }
          else={
            <If
              condition={!transactions?.length}
              then={
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Receipt size={48} className="mb-3 opacity-50" />
                  <p className="font-medium">No transactions yet</p>
                  <p className="text-sm">
                    Add funds to get started with channel leads
                  </p>
                </div>
              }
              else={
                <div className="divide-y divide-gray-50">
                  {transactions?.map((tx: ChannelTransaction) => {
                    const isCredit = parseInt(tx.amount_cents) > 0
                    return (
                      <div
                        key={tx.id}
                        className="px-6 py-4 hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-gray-100 rounded-xl">
                              {getTransactionIcon(tx.type)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {getTransactionLabel(tx.type)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {formatDate(tx.created)}
                                {tx.channel_id !== "0" && (
                                  <span className="ml-2 text-gray-400">
                                    • {getChannelName(tx.channel_id)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-semibold tabular-nums ${
                                isCredit ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              {isCredit ? "+" : ""}$
                              {(parseInt(tx.amount_cents) / 100).toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-400">
                              Balance: $
                              {(parseInt(tx.balance_after_cents) / 100).toFixed(
                                2
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              }
            />
          }
        />
      </div>

      {/* Add Funds Modal */}
      <Modal
        title="Add Funds"
        show={showAddFundsModal}
        onClose={() => setShowAddFundsModal(false)}
      >
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Add funds to your channel balance. An invoice will be created and
            charged to your default payment method.
          </p>

          <div className="mb-6">
            <label
              htmlFor="amount"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">
                $
              </span>
              <input
                id="amount"
                type="text"
                value={addFundsAmount}
                onChange={e => setAddFundsAmount(e.target.value)}
                placeholder="50.00"
                className="appearance-none block w-full pl-10 pr-4 py-3 text-lg border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <LightBtn
              onClick={() => setShowAddFundsModal(false)}
              disabled={isAddingFunds}
            >
              Cancel
            </LightBtn>
            <PrimaryBtn onClick={handleAddFunds} disabled={isAddingFunds}>
              {isAddingFunds ? "Processing..." : "Add Funds"}
            </PrimaryBtn>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default BalanceTab
